/**
 * Utility functions for handling DataTable border styling logic
 * Centralizes border logic to reduce repetition and improve maintainability
 */

export type BorderConfig = {
  isFirstColumn: boolean;
  isLastColumn: boolean;
  isLastRow?: boolean;
  betweenCellBorders: boolean;
  hasRowErrorBorder?: boolean;
  isAction?: boolean;
};

/**
 * Generates base border classes that are common to all table cells
 */
export const getBaseBorderClasses = () => {
  return 'border-b border-b-gray-200 border-r border-r-gray-200';
};

/**
 * Generates right border classes based on configuration
 */
export const getRightBorderClasses = ({
  betweenCellBorders,
  isLastColumn,
  hasRowErrorBorder = false,
}: Pick<BorderConfig, 'betweenCellBorders' | 'isLastColumn' | 'hasRowErrorBorder'>) => {
  const classes: Array<string> = [];

  if (hasRowErrorBorder) {
    return classes;
  }
  if (isLastColumn || !betweenCellBorders) {
    classes.push('border-r-transparent');
  }
  return classes;
};

/**
 * Generates bottom border classes based on configuration
 */
export const getBottomBorderClasses = ({
  isLastRow = false,
  hasRowErrorBorder = false,
}: Pick<BorderConfig, 'isLastRow' | 'hasRowErrorBorder'>) => {
  const classes: Array<string> = [];

  // Hide bottom border on last row unless row has error border
  if (isLastRow && !hasRowErrorBorder) {
    classes.push('border-b-transparent');
  }

  return classes;
};

/**
 * Generates rounded corner classes for table styling
 */
export const getRoundedCornerClasses = ({
  isFirstColumn,
  isLastColumn,
  isLastRow = false,
}: Pick<BorderConfig, 'isFirstColumn' | 'isLastColumn' | 'isLastRow'>) => {
  const classes: Array<string> = [];

  if (isLastRow) {
    if (isFirstColumn) {
      classes.push('rounded-bl-md');
    }
    if (isLastColumn) {
      classes.push('rounded-br-md');
    }
  }

  return classes;
};

/**
 * Generates header-specific rounded corner classes
 */
export const getHeaderRoundedCornerClasses = ({
  isFirstColumn,
  isLastColumn,
}: Pick<BorderConfig, 'isFirstColumn' | 'isLastColumn'>) => {
  const classes: Array<string> = [];

  if (isFirstColumn) {
    classes.push('rounded-tl-md');
  }
  if (isLastColumn) {
    classes.push('rounded-tr-md');
  }

  return classes;
};

/**
 * Generates focus-related border classes
 */
export const getFocusBorderClasses = ({
  isFirstColumn,
  isLastColumn,
}: Pick<BorderConfig, 'isFirstColumn' | 'isLastColumn'>) => {
  const classes: Array<string> = [];

  if (isFirstColumn) {
    classes.push('focus-within:border-l');
  }
  if (isLastColumn) {
    classes.push('focus-within:border-r');
  }

  return classes;
};

/**
 * Generates error border classes when row has errors
 */
export const getErrorBorderClasses = ({
  hasRowErrorBorder = false,
  isFirstColumn,
  isLastColumn,
}: Pick<BorderConfig, 'hasRowErrorBorder' | 'isFirstColumn' | 'isLastColumn'>) => {
  const classes: Array<string> = [];

  if (hasRowErrorBorder) {
    // Apply red borders to top and bottom for all cells
    classes.push('!border-t !border-t-red-500 !border-b-red-500');

    // Apply red left border only to first column
    if (isFirstColumn) {
      classes.push('!border-l !border-l-red-500');
    }

    // Apply red right border only to last column, hide for others
    if (isLastColumn) {
      classes.push('!border-r !border-r-red-500');
    } else {
      classes.push('!border-r-transparent');
    }
  }

  return classes;
};

/**
 * Generates action column size classes
 */
export const getActionColumnClasses = ({ isAction = false }: Pick<BorderConfig, 'isAction'>) => {
  const classes: Array<string> = [];

  if (isAction) {
    classes.push('w-[40px] max-w-[40px]');
  }

  return classes;
};

/**
 * Main function to generate all cell border classes
 * Combines all border utilities for table body cells
 */
export const getCellBorderClasses = (config: BorderConfig): Array<string> => {
  return [
    ...getRightBorderClasses(config),
    ...getBottomBorderClasses(config),
    ...getRoundedCornerClasses(config),
    ...getFocusBorderClasses(config),
    ...getErrorBorderClasses(config),
    ...getActionColumnClasses(config),
  ];
};

/**
 * Main function to generate all header border classes
 * Combines all border utilities for table header cells
 */
export const getHeaderBorderClasses = (
  config: Omit<BorderConfig, 'isLastRow' | 'hasRowErrorBorder'>,
): Array<string> => {
  return [
    ...getRightBorderClasses({ ...config, hasRowErrorBorder: false }),
    ...getHeaderRoundedCornerClasses(config),
    ...getActionColumnClasses(config),
  ];
};
